/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { mexCoreBaseApi } from "./baseApis"
import {
  MENU_MIGRATION_CHECKLIST_MILESTONE_KEY,
  MIGRATION_CHECKLIST_STATUS_TIMEOUT_MS,
  mexCoreApiEndpoints,
} from "./constants"

export interface MigrationChecklistMerchantStatus {
  merchant_id: string
  is_completed: boolean
  notCompletedMilestoneKeys?: string[]
}

export interface MigrationChecklistStatusResponse {
  merchant_statuses?: MigrationChecklistMerchantStatus[] | null
}

export interface GetMigrationChecklistStatusRequest {
  merchantIDs: string[]
  milestoneKeys?: string[]
}

/**
 * GET /v1/garnet/migration-checklist
 * `incompleted_stores` is the eligibility / cohort signal Andros is adjusting on BE.
 * Other fields exist on the live contract today (`should_show`, etc.).
 */
export interface MigrationChecklistResponse {
  should_show?: boolean
  merchant_group_id?: string
  title?: string
  subtitle?: string
  is_completed?: boolean
  /**
   * Store IDs still incomplete for the parent checklist cohort.
   * Presence of this array (including empty) means the merchant is eligible.
   * Optional until BE ships Andros' small contract adjustment — FE has a
   * temporary fallback in resolveIsEligibleMerchantFromChecklist.
   */
  incompleted_stores?: string[] | null
}

export const garnetApi = mexCoreBaseApi.injectEndpoints({
  endpoints: builder => ({
    getMigrationChecklist: builder.query<MigrationChecklistResponse, void>({
      query: () => ({
        url: mexCoreApiEndpoints.migrationChecklist,
        method: "GET",
        // Eligibility gate owns this failure path (inline retry with status).
        extraOptions: { showError: false },
      }),
    }),
    getMigrationChecklistStatus: builder.query<
      MigrationChecklistStatusResponse,
      GetMigrationChecklistStatusRequest
    >({
      query: ({
        merchantIDs,
        milestoneKeys = [MENU_MIGRATION_CHECKLIST_MILESTONE_KEY],
      }) => ({
        url: mexCoreApiEndpoints.migrationChecklistStatus,
        method: "POST",
        data: {
          merchant_ids: merchantIDs,
          milestone_keys: milestoneKeys,
        },
        timeout: MIGRATION_CHECKLIST_STATUS_TIMEOUT_MS,
        // Inline error + retry UI owns this failure path.
        extraOptions: { showError: false },
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        const sortedMerchantIDs = [...(queryArgs.merchantIDs || [])].sort()
        const sortedMilestoneKeys = [
          ...(queryArgs.milestoneKeys || [
            MENU_MIGRATION_CHECKLIST_MILESTONE_KEY,
          ]),
        ].sort()
        return {
          merchantIDs: sortedMerchantIDs,
          milestoneKeys: sortedMilestoneKeys,
        }
      },
    }),
  }),
})

export const {
  useGetMigrationChecklistQuery,
  useGetMigrationChecklistStatusQuery,
} = garnetApi
